import type { SectionState } from '../store/editor.store'
import { uid, sec, blk } from './siteTemplates.helpers'
export { uid, sec, blk }

export interface ColorVariant {
  id: string
  label: string
  bg: string
  accent: string
  ink: string
  pages: Array<{
    title: string
    slug: string
    isHome: boolean
    sections: SectionState[]
  }>
}

export interface StyleVariant {
  id: string
  label: string
  description: string
  colorVariants?: ColorVariant[]
  pages: Array<{
    title: string
    slug: string
    isHome: boolean
    sections: SectionState[]
  }>
}

export interface SiteTemplate {
  id: string
  label: string
  description: string
  icon: string
  pages: Array<{
    title: string
    slug: string
    isHome: boolean
    sections: SectionState[]
  }>
  variants?: StyleVariant[]
}

// ── Templates ─────────────────────────────────────────────────────────────────

const LANDING_TEMPLATE: SiteTemplate = {
  id: 'landing',
  label: 'Лендинг',
  description: 'Продающая страница с преимуществами и формой',
  icon: '🚀',
  pages: [
    {
      title: 'Главная',
      slug: '/',
      isHome: true,
      sections: [
        sec([blk('navbar', { logo: 'ProductName', links: [{ label: 'Возможности', url: '/features' }, { label: 'Цены', url: '/pricing' }, { label: 'Контакты', url: '/contact' }], ctaText: 'Попробовать', ctaUrl: '#' })]),
        sec([blk('hero-centered', {
          title: 'Управляйте проектами в 3 раза быстрее',
          subtitle: 'Мощная платформа для команд, которые хотят делать больше. Тысячи клиентов уже экономят часы каждый день.',
          primaryButton: { text: 'Начать бесплатно', url: '#' },
          secondaryButton: { text: 'Посмотреть демо', url: '#' },
        })]),
        sec([blk('guarantee-bar', { items: [{ icon: '✓', text: '14 дней бесплатно' }, { icon: '✓', text: 'Без карты' }, { icon: '✓', text: 'Отмена в любое время' }, { icon: '✓', text: '24/7 поддержка' }] })]),
        sec([blk('features', { title: 'Всё что нужно для роста', columns: 3, items: [
          { icon: '⚡', title: 'Быстрый старт', description: 'Настройте рабочее пространство за 5 минут и сразу приступайте к работе' },
          { icon: '🎯', title: 'Точный результат', description: 'Инструменты для управления задачами, командой и дедлайнами в одном месте' },
          { icon: '📊', title: 'Аналитика роста', description: 'Отслеживайте прогресс и принимайте решения на основе реальных данных' },
        ]})], { paddingTop: 60, paddingBottom: 60 }),
        sec([blk('testimonials', { title: 'Что говорят клиенты', testimonials: [
          { name: 'Анна К.', role: 'CEO, StartupRu', text: 'Внедрили за 1 день. Производительность команды выросла на 40% в первый же месяц!' },
          { name: 'Михаил В.', role: 'Руководитель проекта', text: 'Наконец-то один инструмент вместо пяти. Простой, мощный, без лишнего.' },
          { name: 'Ольга Л.', role: 'Операционный директор', text: 'Автоматизировали рутину и сэкономили 15 часов в неделю.' },
        ]})], { background: '#f8f9ff', paddingTop: 60, paddingBottom: 60 }),
        sec([blk('cta', { title: 'Начните прямо сейчас', subtitle: 'Первые 14 дней — бесплатно. Кредитная карта не нужна.', buttonText: 'Попробовать бесплатно', buttonUrl: '#' })]),
        sec([blk('footer', { brand: 'ProductName', links: [{ label: 'Политика', url: '#' }, { label: 'Условия', url: '#' }], copyright: '© 2026 ProductName. Все права защищены.' })]),
      ],
    },
    {
      title: 'Возможности',
      slug: '/features',
      isHome: false,
      sections: [
        sec([blk('navbar', { logo: 'ProductName', links: [{ label: 'Возможности', url: '/features' }, { label: 'Цены', url: '/pricing' }, { label: 'Контакты', url: '/contact' }], ctaText: 'Попробовать', ctaUrl: '#' })]),
        sec([blk('features', { title: 'Все возможности продукта', subtitle: 'Инструменты для роста вашего бизнеса', columns: 3, items: [
          { icon: '⚡', title: 'Быстрый старт', description: 'Начните работу за 5 минут — настройка без технических знаний' },
          { icon: '🎯', title: 'Точное управление', description: 'Задачи, сроки, приоритеты — всё под контролем в одном месте' },
          { icon: '📊', title: 'Аналитика', description: 'Понятные дашборды и отчёты в реальном времени' },
          { icon: '🔒', title: 'Безопасность', description: 'Шифрование данных и защита доступа по умолчанию' },
          { icon: '📱', title: 'Везде и всегда', description: 'Работайте с любого устройства — телефона, планшета или ноутбука' },
          { icon: '🤝', title: 'Поддержка 24/7', description: 'Команда всегда готова помочь с любым вопросом' },
        ]})], { paddingTop: 60, paddingBottom: 60 }),
        sec([blk('testimonials', { title: 'Клиенты о нас', testimonials: [
          { name: 'Анна К.', role: 'CEO, StartupRu', text: 'Команда стала работать на 40% эффективнее уже в первый месяц!' },
          { name: 'Михаил В.', role: 'Руководитель проекта', text: 'Наконец инструмент, который делает именно то, что обещает.' },
        ]})], { background: '#f8f9ff', paddingTop: 60, paddingBottom: 60 }),
        sec([blk('cta', { title: 'Готовы попробовать?', subtitle: 'Первые 14 дней бесплатно — без карты.', buttonText: 'Начать бесплатно', buttonUrl: '#' })]),
        sec([blk('footer', { brand: 'ProductName', links: [{ label: 'Политика', url: '#' }, { label: 'Условия', url: '#' }], copyright: '© 2026 ProductName. Все права защищены.' })]),
      ],
    },
    {
      title: 'Цены',
      slug: '/pricing',
      isHome: false,
      sections: [
        sec([blk('navbar', { logo: 'ProductName', links: [{ label: 'Возможности', url: '/features' }, { label: 'Цены', url: '/pricing' }, { label: 'Контакты', url: '/contact' }], ctaText: 'Попробовать', ctaUrl: '#' })]),
        sec([blk('pricing', { title: 'Прозрачные цены', subtitle: 'Без скрытых платежей. Отмена в любое время.', plans: [
          { name: 'Старт', price: 'Бесплатно', period: 'навсегда', features: ['До 5 пользователей', 'Базовые функции', 'Свой домен', 'Email поддержка'], ctaText: 'Начать бесплатно', highlighted: false },
          { name: 'Про', price: '990₽', period: 'в месяц', features: ['До 20 пользователей', 'Все функции', 'Интеграции', 'Приоритетная поддержка', 'Аналитика'], ctaText: 'Попробовать 14 дней', highlighted: true },
          { name: 'Бизнес', price: '2 490₽', period: 'в месяц', features: ['Без ограничений', 'Командный доступ', 'API', 'Белый лейбл', 'SLA'], ctaText: 'Связаться', highlighted: false },
        ]})], { paddingTop: 60, paddingBottom: 60 }),
        sec([blk('footer', { brand: 'ProductName', links: [{ label: 'Политика', url: '#' }, { label: 'Условия', url: '#' }], copyright: '© 2026 ProductName. Все права защищены.' })]),
      ],
    },
    {
      title: 'Контакты',
      slug: '/contact',
      isHome: false,
      sections: [
        sec([blk('navbar', { logo: 'ProductName', links: [{ label: 'Возможности', url: '/features' }, { label: 'Цены', url: '/pricing' }, { label: 'Контакты', url: '/contact' }], ctaText: 'Попробовать', ctaUrl: '#' })]),
        sec([blk('contact-block', { title: 'Свяжитесь с нами', text: 'Ответим в течение рабочего дня. Или напишите на почту hello@productname.ru.', buttonText: 'Отправить' })], { paddingTop: 80, paddingBottom: 80 }),
        sec([blk('footer', { brand: 'ProductName', links: [{ label: 'Политика', url: '#' }, { label: 'Условия', url: '#' }], copyright: '© 2026 ProductName. Все права защищены.' })]),
      ],
    },
  ],
}

import { TEMPLATE_VARIANTS } from './siteTemplates.styles'

export const SITE_TEMPLATES: SiteTemplate[] = [
  { ...LANDING_TEMPLATE, variants: TEMPLATE_VARIANTS['landing'].filter(v => v.id === 'bento-landing') },
]
